import React, { useEffect } from "react";
import toast from 'react-hot-toast';
import { Controller, FormProvider, useFormContext, useForm, useWatch } from 'react-hook-form';
import { DialogTitle } from '@mui/material';
import { BootstrapDialog } from 'components/languageTiers/AddLanguageTiers';
import ErrorMessage from 'components/common/ErrorMessage';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import Api from 'services/api';

const apiService = new Api();

const CustomTranslationWorkOrderModal = ({ item, show, onClose, onSuccess }) => {
  const defaultValues = {
    translation_rate: "",
    editing_rate: "",
    dtp_rate: "",
    review_rate: "",
    audio_recording_rate: "",
    transcription_rate: "",
  };

  const methods = useForm({
    defaultValues
  });

  const onSubmit = async (data) => {
    try {
      data.translation_id = item?.translation_id;
      data.interpreter_id = item?.interpreter?.id;

      const response = await Api.setTranslatorRates(data);
      
      if (response && response.success) {
        toast.success(response.message || 'Custom work order created successfully');
        methods.reset(defaultValues);
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response?.message || 'Failed to create custom work order');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while creating the work order');
    }
  };

  return (
    <BootstrapDialog
      fullWidth
      maxWidth="md"
      open={show}
      onClose={(e, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      sx={{
        "& .MuiDialog-container": { alignItems: "flex-start", padding: "2rem" },
        "& .MuiPaper-root": { overflowY: "visible", margin: "auto" },
      }}
    >
      <div className="modal-content full_modal">
        <DialogTitle>
          <div className="modal-header header_modal align-items-center">
            <div></div>
            <h5 className="modal-title_u">Custom Translation Work Order</h5>
            <button onClick={onClose} type="button" className="btn-close" />
          </div>
        </DialogTitle>
        <div className="modal-body modal_form_body">
          <FormProvider {...methods}>
            <CustomTranslationWorkOrder
              item={item}
              onClose={onClose}
              onSubmit={methods.handleSubmit(onSubmit)}
            />
          </FormProvider>
        </div>
      </div>
    </BootstrapDialog>
  );
};

const CustomTranslationWorkOrder = ({ item, onClose, onSubmit }) => {
  const { control, formState: { errors }, setValue } = useFormContext();
  const translation_rate = useWatch({ control, name: "translation_rate" });
  const editing_rate = useWatch({ control, name: "editing_rate" });
  const dtp_rate = useWatch({ control, name: "dtp_rate" });
  const review_rate = useWatch({ control, name: "review_rate" });
  const audio_recording_rate = useWatch({ control, name: "audio_recording_rate" });
  const transcription_rate = useWatch({ control, name: "transcription_rate" });

  // Fetch translator rates on mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await Api.getTranslatorRates({
          interpreter_id: item?.interpreter?.id,
          translation_id: item?.translation_id,
        });
        
        if (response && response.success && response.data) {
          const { translation_rate, editing_rate, dtp_rate, review_rate, audio_recording_rate, transcription_rate } = response.data;
          
          setValue("translation_rate", translation_rate || 0);
          setValue("editing_rate", editing_rate || 0);
          setValue("dtp_rate", dtp_rate || 0);
          setValue("review_rate", review_rate || 0);
          setValue("audio_recording_rate", audio_recording_rate || 0);
          setValue("transcription_rate", transcription_rate || 0);
        }
      } catch (error) {
        console.error("Error fetching translator rates:", error);
      }
    };

    if (item?.interpreter?.id) {
      fetchRates();
    }
  }, [item?.interpreter?.id, setValue]);

  const renderRateField = (fieldName, label, placeholder) => (
    <div className="col-md-6 mb-3">
      <div className="row-primary mb-2">
        <h2>{label}</h2>
      </div>
      <Controller
        name={fieldName}
        control={control}
        rules={{
          required: `${label} is required`,
          min: { value: 0, message: "Value must be at least 0" },
          pattern: {
            value: /^\d+(\.\d{1,2})?$/,
            message: "Up to 2 decimals allowed",
          },
        }}
        render={({ field }) => (
          <div className="input-image">
            <input 
              type="number" 
              placeholder={placeholder} 
              {...field} 
              min={0}
              step="0.01"
            />
          </div>
        )}
      />
      {errors[fieldName]?.message && (
        <ErrorMessage text={errors[fieldName].message} />
      )}
    </div>
  );

  return (
    <div className="step_cards">
      <div className="w-100 custom_pricing">
        <div className="row d-flex justify-content-between">
          {renderRateField("translation_rate", "Translation per Word Rate", "Enter rate per word")}
          {renderRateField("editing_rate", "Editing per Word Rate", "Enter rate per word")}
        </div>

        <div className="row d-flex justify-content-between">
          {renderRateField("dtp_rate", "DTP per Word Rate", "Enter rate per word")}
          {renderRateField("review_rate", "Review per Page Rate", "Enter rate per page")}
        </div>

        <div className="row d-flex justify-content-between">
          {renderRateField("audio_recording_rate", "Audio Recording per Word Rate", "Enter rate per word")}
          {renderRateField("transcription_rate", "Transcription per Audio Minute Rate", "Enter rate per minute")}
        </div>

        <div className="row d-flex justify-content-between mb-3 mt-4">
          <div className="col-md-12">
            <div className="Form-footer">
              <button type="button" className="Cancle_button" onClick={onClose}>Cancel</button>
              <SubmitButton
                btnText={"Create"}
                handleSubmit={onSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomTranslationWorkOrderModal;
